import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { BaseLink } from "shared/components/common/base-link";
import { setFullViewportWidthStyles, theme, transientOptions } from "shared/lib/styles";

import { Header } from "scmp-app/components/header";
import { HeaderOnelineMenuContainer } from "scmp-app/components/header/header-oneline-menu-container";
import { MegaMenuWrapper } from "scmp-app/components/header/header-oneline-menu-container/header-all-menu/styles";
import IconArrowNextMobile from "scmp-app/components/header/header-oneline-menu-container/header-oneline-menu/icon-arrow-next-mobile.svg?url";
import IconArrowNext from "scmp-app/components/header/header-oneline-menu-container/header-oneline-menu/icon-arrow-next.svg?url";
import IconArrowPreviousMobile from "scmp-app/components/header/header-oneline-menu-container/header-oneline-menu/icon-arrow-previous-mobile.svg?url";
import IconArrowPrevious from "scmp-app/components/header/header-oneline-menu-container/header-oneline-menu/icon-arrow-previous.svg?url";
import {
  StyledSwiper,
  StyledSwiperSlide,
} from "scmp-app/components/header/header-oneline-menu-container/header-oneline-menu/styles";
import {
  AllMenuContainer,
  Divider,
} from "scmp-app/components/header/header-oneline-menu-container/styles";

import Logo from "./icon-logo.svg";

export const Container = styled.div`
  ${setFullViewportWidthStyles()}
`;

export const HomeHeaderContainer = styled.section``;

export const StyledLogo = styled(Logo)`
  inline-size: 207px;
  block-size: 22px;
  ${theme.breakpoints.up("tablet")} {
    inline-size: 330px;
    block-size: 34.3px;
  }

  ${theme.breakpoints.up("desktop")} {
    inline-size: 481px;
    block-size: 50px;
  }
`;

export const StyledLogoLink = styled(BaseLink)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  ${theme.breakpoints.up("tablet")} {
    align-items: flex-start;
  }
`;

export const HeaderContainer = styled.div`
  position: relative;

  background-color: transparent;

  ${theme.breakpoints.up("desktop")} {
    border-block-end: 1px solid #000000;
  }
`;

export const Wrapper = styled.div`
  position: relative;

  max-inline-size: 1344px;
  margin-inline: auto;

  ${theme.breakpoints.up("tablet")} {
    padding-block: 16px 0;
    padding-inline: 0;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-block: 24px 0;
  }
`;

export const Content = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr max-content 1fr;
  align-items: center;

  inline-size: 100%;
  block-size: 45px;
  padding-inline: 20px;
  box-sizing: border-box;

  ${theme.breakpoints.up("tablet")} {
    block-size: 60px;
    padding-inline: 32px;
  }

  ${theme.breakpoints.up("desktop")} {
    block-size: 64px;
    margin-block-end: 24px;
  }
`;

export const LeftFeatureContainer = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;

  & > *:not(:last-child) {
    margin-inline-end: 20px;
  }
`;

export const RightFeatureContainer = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
  justify-content: flex-end;
  align-items: center;

  ${theme.breakpoints.up("tablet")} {
    padding-inline-start: 20px;
  }
`;

export const OnelineMenuContainer = styled.div`
  display: flex;
  justify-content: center;

  block-size: 34px;
  margin-inline: 20px;
  border-block-end: 1px solid #000000;

  ${theme.breakpoints.between("tablet", "desktop")} {
    margin-inline: 32px;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-inline: 0;
    border-block-end: none;
  }
`;

export const StyledHeaderOnelineMenuContainer = styled(HeaderOnelineMenuContainer)`
  display: flex;
  justify-content: center;
  align-items: center;

  margin-inline-start: 0;
  padding-block: 10px;

  ${AllMenuContainer} {
    display: none;

    ${theme.breakpoints.up("desktop")} {
      display: flex;
    }
  }

  ${StyledSwiperSlide} {
    &:not(:last-child) {
      margin-inline-end: 16px;

      ${theme.breakpoints.up("desktop")} {
        margin-inline-end: 14px;
      }
    }
  }

  ${MegaMenuWrapper} {
    margin-block-start: 11px;
  }

  ${StyledSwiper} {
    .swiper-wrapper {
      block-size: auto;
    }

    .swiper-button-prev {
      margin-inline-start: -9px;

      background-image: url(${IconArrowPreviousMobile});

      ${theme.breakpoints.up("desktop")} {
        background-image: url(${IconArrowPrevious});
      }
    }

    .swiper-button-next {
      margin-inline-end: -9px;

      background-image: url(${IconArrowNextMobile});

      ${theme.breakpoints.up("desktop")} {
        background-image: url(${IconArrowNext});
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      inline-size: 36px;
      block-size: 17px;

      border-radius: 0;

      background-color: transparent;
      background-size: unset;

      opacity: 1;
    }
  }

  ${Divider} {
    margin-inline: 14px;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-block: 8px 10px;
  }
`;

type StyledHomeMiniHeaderProps = {
  $shouldSwap: boolean | null;
};

export const StyledHomeMiniHeader = styled(Header, {
  ...transientOptions,
})<StyledHomeMiniHeaderProps>`
  position: fixed;
  inset-block-start: -60px;

  background-color: #ffffff;

  ${props =>
    props.$shouldSwap === true
      ? css`
          inset-block-start: 236px;

          transition: inset-block-start 0.4s;

          ${theme.breakpoints.up("tablet")} {
            inset-block-start: 297px;
          }

          ${theme.breakpoints.up("desktop")} {
            inset-block-start: 508px;
          }
        `
      : null}
`;
